import {
  Avatar,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Send, HowToReg, CheckCircle, Badge } from "@mui/icons-material";
import axios from "axios";
import SidebarActionItemCard from "./SidebarActionItemCard";
import { BookingData } from "../../types/view-booking";
import { SidebarActionItem } from "../types/sidebar.types";
import { CalendarIcon } from "../../../../components/icons/CalendarIcon";
import { MessageIcon } from "../../../../components/icons/MessageIcon";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import RequestClientReviewModal from "./RequestClientReviewModal";
import ThankYouMessageModal from "./ThankYouMessageModal";
import AnniversaryMessageModal from "./AnniversaryMessageModal";
import RequestStatusChangeModal from "./RequestStatusChangeModal";
import AssignSalesRepModal from "./AssignSalesRepModal";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../../components/common/ShowToast";
import {
  BookingStatusChangeRequest,
  createStatusChangeRequest,
  fetchBookingStatusChangeRequests,
  approveStatusChangeRequest,
  declineStatusChangeRequest,
} from "./statusChangeRequestStorage";
import { assignSalesRep, SalesRep } from "./salesRepStorage";
import { BaseURL } from "../../../../service/Api";

const getMediaUrl = (path?: string | null) => {
  if (!path) return undefined;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const cleanBaseUrl = BaseURL.replace(/\/$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${cleanBaseUrl}${cleanPath}`;
};

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const SEEN_RESOLUTION_KEY = "seen-status-change-resolutions";

const readSeen = (): number[] => {
  try {
    return JSON.parse(sessionStorage.getItem(SEEN_RESOLUTION_KEY) || "[]");
  } catch {
    return [];
  }
};

const markSeen = (requestId: number) => {
  const seen = readSeen();
  if (!seen.includes(requestId)) {
    sessionStorage.setItem(
      SEEN_RESOLUTION_KEY,
      JSON.stringify([...seen, requestId]),
    );
  }
};

interface BookingSidebarProps {
  bookingData?: BookingData | null;
  onOpenAIAssistant?: (mode: "review" | "thankyou" | "anniversary") => void;
}

export default function BookingSidebar({
  bookingData,
  onOpenAIAssistant,
}: BookingSidebarProps) {
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [thankYouModalOpen, setThankYouModalOpen] = useState(false);
  const [anniversaryModalOpen, setAnniversaryModalOpen] = useState(false);
  const [statusChangeModalOpen, setStatusChangeModalOpen] = useState(false);
  const [assignSalesRepModalOpen, setAssignSalesRepModalOpen] = useState(false);
  const [decidingRequest, setDecidingRequest] = useState(false);
  const [decliningReason, setDecliningReason] = useState<string | null>(null);
  const [justConfirmed, setJustConfirmed] = useState(false);
  const [salesRep, setSalesRep] = useState<SalesRep | null>(null);

  const currentUser = useSelector((state: RootState) => state.auth.user);

  const bookingId = bookingData?.id;

  useEffect(() => {
    setSalesRep(
      bookingData?.sales_rep
        ? {
            id: bookingData.sales_rep.id,
            full_name:
              `${bookingData.sales_rep.first_name} ${bookingData.sales_rep.last_name}`.trim() ||
              bookingData.sales_rep.username,
            email: bookingData.sales_rep.email,
            profile_picture_url: bookingData.sales_rep.profile_picture_url,
          }
        : null,
    );
  }, [bookingData?.sales_rep]);

  const handleAssignSalesRep = async (salesRepId: number) => {
    if (!bookingId) return;
    try {
      const rep = await assignSalesRep(bookingId, salesRepId);
      setSalesRep(rep);
      showSuccessToast(`${rep.full_name} assigned as sales rep.`);
    } catch {
      showErrorToast("Failed to assign sales rep. Please try again.");
    }
  };

  const [latestRequest, setLatestRequest] =
    useState<BookingStatusChangeRequest | null>(null);

  useEffect(() => {
    if (!bookingId) {
      setLatestRequest(null);
      return;
    }

    let cancelled = false;

    const check = async () => {
      try {
        const requests = await fetchBookingStatusChangeRequests(bookingId);
        if (cancelled) return;
        const latest = requests[0] || null;
        setLatestRequest(latest);

        if (latest && latest.status === "declined") {
          const seen = readSeen();
          if (!seen.includes(latest.id)) {
            showErrorToast(
              latest.decision_reason
                ? `Declined by ${latest.decided_by?.first_name || "an admin"}: ${latest.decision_reason}`
                : `Declined by ${latest.decided_by?.first_name || "an admin"}.`,
            );
            markSeen(latest.id);
          }
        }
      } catch {
        // one-off fetch failure -- silently ignored, page just shows stale state until reload
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [bookingId]);

  const handleRequestStatusChange = async (
    approverIds: number[],
    reason: string,
  ) => {
    if (!bookingId) return;
    try {
      const created = await createStatusChangeRequest(
        bookingId,
        "confirmed",
        approverIds,
        reason,
      );
      setLatestRequest(created);
      showSuccessToast("Approval request sent.");
    } catch (err) {
      const data = axios.isAxiosError(err)
        ? (err.response?.data as
            | { detail?: string; reason?: string[] }
            | undefined)
        : undefined;
      const detail =
        data?.detail ||
        data?.reason?.[0] ||
        "Failed to send request. Please try again.";
      showErrorToast(detail);
    }
  };

  const handleApprove = async () => {
    if (!latestRequest) return;
    try {
      setDecidingRequest(true);
      const updated = await approveStatusChangeRequest(latestRequest.id);
      setLatestRequest(updated);
      setJustConfirmed(true);
      showSuccessToast("Booking confirmed.");
    } catch {
      showErrorToast("Failed to confirm booking.");
    } finally {
      setDecidingRequest(false);
    }
  };

  const handleDeclineConfirm = async () => {
    if (!latestRequest) return;
    try {
      setDecidingRequest(true);
      const updated = await declineStatusChangeRequest(
        latestRequest.id,
        (decliningReason || "").trim(),
      );
      setLatestRequest(updated);
      markSeen(updated.id);
      setDecliningReason(null);
      showSuccessToast("Request declined.");
    } catch {
      showErrorToast("Failed to decline request.");
    } finally {
      setDecidingRequest(false);
    }
  };

  const serviceType = (bookingData?.service || "funeral").toLowerCase();
  const isFuneral = serviceType === "funeral";

  const deceased = bookingData?.beloved;
  const dateOfDeath = deceased?.details?.dateOfDeath;

  const getDaysUntilAnniversary = (dod: string): number | null => {
    if (!dod || dod === "N/A") return null;
    const [day, month, year] = dod.split("-");
    const death = new Date(Number(year), Number(month) - 1, Number(day));
    if (isNaN(death.getTime())) return null;
    const today = new Date();
    const next = new Date(
      today.getFullYear(),
      death.getMonth(),
      death.getDate(),
    );
    if (next <= today) next.setFullYear(today.getFullYear() + 1);
    return Math.ceil(
      (next.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );
  };

  const daysUntil = dateOfDeath ? getDaysUntilAnniversary(dateOfDeath) : null;

  const actionItems: SidebarActionItem[] = [];

  if (isFuneral) {
    actionItems.push({
      icon: <CalendarIcon sx={{ color: "#007AFF" }} />,
      title:
        daysUntil !== null
          ? `${daysUntil} days until ${deceased?.name || "beloved"}'s anniversary`
          : "No anniversary data available",
      subtitle: "Send message to family",
      onClick: () => {
        if (onOpenAIAssistant) {
          onOpenAIAssistant("anniversary");
        } else {
          setAnniversaryModalOpen(true);
        }
      },
    });
  }

  if (!salesRep) {
    actionItems.push({
      icon: <Badge sx={{ color: "#724E41" }} />,
      title: "Assign Sales Rep",
      subtitle: "Choose a sales rep to own this booking",
      onClick: () => setAssignSalesRepModalOpen(true),
    });
  }

  actionItems.push(
    {
      icon: <MessageIcon sx={{ color: "#E11D48" }} />,
      title: "Request Client review",
      subtitle: "Gather insights from client for future improvements",
      onClick: () => {
        if (onOpenAIAssistant) {
          onOpenAIAssistant("review");
        } else {
          setReviewModalOpen(true);
        }
      },
    },
    {
      icon: <Send sx={{ color: "#724E41" }} />,
      title: "Send Thank you message to client",
      subtitle: "Reflect on project outcomes and team",
      onClick: () => {
        if (onOpenAIAssistant) {
          onOpenAIAssistant("thankyou");
        } else {
          setThankYouModalOpen(true);
        }
      },
    },
  );

  const isConfirmed =
    justConfirmed ||
    ["confirmed", "completed"].includes(
      (bookingData?.status || "").toLowerCase(),
    );

  const isAdmin = currentUser?.role === "admin";
  const isPending = latestRequest?.status === "pending";

  const isSelectedApprover =
    isPending &&
    !!latestRequest &&
    latestRequest.approvers.some(
      (a) => a.email.toLowerCase() === currentUser?.username?.toLowerCase(),
    );

  const showApproveCard = !isConfirmed && isSelectedApprover;

  if (isConfirmed) {
    actionItems.push({
      icon: <CheckCircle sx={{ color: "#4BA663" }} />,
      title: "Booking Confirmed",
      subtitle: "This booking has been confirmed",
    });
  } else if (isPending && latestRequest) {
    if (!isSelectedApprover) {
      const names = latestRequest.approvers
        .map((a) => `${a.first_name} ${a.last_name}`.trim() || a.email)
        .join(", ");
      actionItems.push({
        icon: <HowToReg sx={{ color: "#F79E1B" }} />,
        title: "Approval Pending",
        subtitle: `Waiting on ${names}`,
      });
    }
  } else if (isAdmin) {
    // Admins confirm bookings directly elsewhere; no action item needed here.
  } else {
    actionItems.push({
      icon: <HowToReg sx={{ color: "#4BA663" }} />,
      title: "Request Status Change",
      subtitle: "Ask an admin to confirm this booking",
      onClick: () => setStatusChangeModalOpen(true),
    });
  }

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Stack spacing={1}>
          {salesRep && (
            <Paper sx={{ borderRadius: "12px", p: 1.5, boxShadow: 0 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "11px",
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  mb: 1,
                }}
              >
                Sales Rep
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <Avatar src={getMediaUrl(salesRep.profile_picture_url)}>
                  {getInitials(salesRep.full_name)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{ fontWeight: 600, fontSize: "13px" }}
                    noWrap
                  >
                    {salesRep.full_name}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "11px", color: "#cbd5e1" }}
                    noWrap
                  >
                    {salesRep.email}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setAssignSalesRepModalOpen(true)}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: 12,
                  color: "#724E41",
                  borderColor: "#724E41",
                  "&:hover": { borderColor: "#5d3e33", bgcolor: "#724E4112" },
                }}
              >
                Reassign
              </Button>
            </Paper>
          )}

          {actionItems.map((item, index) => (
            <SidebarActionItemCard key={index} item={item} />
          ))}

          {showApproveCard && latestRequest && (
            <Paper sx={{ borderRadius: "12px", p: 1.5, boxShadow: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "13px", mb: 0.5 }}>
                Status change requested
              </Typography>
              {latestRequest.requested_by && (
                <Typography
                  sx={{ fontSize: "11px", color: "#cbd5e1", mb: 0.5 }}
                >
                  Requested by{" "}
                  {`${latestRequest.requested_by.first_name} ${latestRequest.requested_by.last_name}`.trim() ||
                    latestRequest.requested_by.username}
                </Typography>
              )}
              <Typography sx={{ fontSize: "11px", color: "#cbd5e1", mb: 1.5 }}>
                {latestRequest.reason}
              </Typography>

              {decliningReason !== null && (
                <TextField
                  autoFocus
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  label="Reason for declining (optional)"
                  value={decliningReason}
                  onChange={(e) => setDecliningReason(e.target.value)}
                  sx={{ mb: 1.5 }}
                />
              )}

              <Stack direction="row" spacing={1}>
                {decliningReason !== null ? (
                  <>
                    <Button
                      variant="outlined"
                      fullWidth
                      disabled={decidingRequest}
                      onClick={() => setDecliningReason(null)}
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: 12,
                      }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={decidingRequest}
                      onClick={handleDeclineConfirm}
                      sx={{
                        bgcolor: "#EB4335",
                        textTransform: "none",
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: 12,
                        boxShadow: "none",
                        "&:hover": { bgcolor: "#C5352A", boxShadow: "none" },
                      }}
                    >
                      Confirm Decline
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={decidingRequest}
                      onClick={handleApprove}
                      sx={{
                        bgcolor: "#4BA663",
                        textTransform: "none",
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: 12,
                        boxShadow: "none",
                        "&:hover": { bgcolor: "#3E8A52", boxShadow: "none" },
                      }}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      disabled={decidingRequest}
                      onClick={() => setDecliningReason("")}
                      sx={{
                        color: "#EB4335",
                        borderColor: "#EB4335",
                        textTransform: "none",
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: 12,
                        "&:hover": {
                          borderColor: "#C5352A",
                          bgcolor: "#EB433512",
                        },
                      }}
                    >
                      Decline
                    </Button>
                  </>
                )}
              </Stack>
            </Paper>
          )}
        </Stack>
      </Box>
      <RequestClientReviewModal
        open={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        bookingData={bookingData}
      />
      <ThankYouMessageModal
        open={thankYouModalOpen}
        onClose={() => setThankYouModalOpen(false)}
        bookingData={bookingData}
      />
      <AnniversaryMessageModal
        open={anniversaryModalOpen}
        onClose={() => setAnniversaryModalOpen(false)}
        bookingData={bookingData}
      />
      <RequestStatusChangeModal
        open={statusChangeModalOpen}
        onClose={() => setStatusChangeModalOpen(false)}
        bookingLabel={
          bookingData?.beloved?.name || bookingData?.organiser?.name
        }
        onSubmit={handleRequestStatusChange}
      />
      <AssignSalesRepModal
        open={assignSalesRepModalOpen}
        onClose={() => setAssignSalesRepModalOpen(false)}
        onSubmit={handleAssignSalesRep}
      />
    </>
  );
}
